import { useCallback, useEffect, useRef, useState } from 'react';

type TooltipAlign = 'center' | 'left' | 'right';
type TooltipPlacement = 'top' | 'bottom';

interface InfoTooltipProps {
  text: string;
  preferredAlign?: TooltipAlign;
  placement?: TooltipPlacement;
}

export function InfoTooltip({ text, preferredAlign, placement = 'top' }: InfoTooltipProps) {
  const wrapperRef = useRef<HTMLSpanElement>(null);
  const tooltipRef = useRef<HTMLSpanElement>(null);
  const [isActive, setIsActive] = useState(false);
  const [align, setAlign] = useState<TooltipAlign>(preferredAlign || 'center');

  const updateAlignment = useCallback(() => {
    if (preferredAlign) {
      setAlign(preferredAlign);
      return;
    }

    const tooltip = tooltipRef.current;
    if (!tooltip) return;

    const rect = tooltip.getBoundingClientRect();
    const viewportPadding = 8;

    if (rect.left < viewportPadding) {
      setAlign('left');
      return;
    }

    if (rect.right > window.innerWidth - viewportPadding) {
      setAlign('right');
      return;
    }

    setAlign('center');
  }, [preferredAlign]);

  useEffect(() => {
    if (!isActive) return;

    const frame = requestAnimationFrame(updateAlignment);
    const handleViewportChange = () => updateAlignment();

    window.addEventListener('resize', handleViewportChange);
    window.addEventListener('scroll', handleViewportChange, true);

    return () => {
      cancelAnimationFrame(frame);
      window.removeEventListener('resize', handleViewportChange);
      window.removeEventListener('scroll', handleViewportChange, true);
    };
  }, [isActive, updateAlignment]);

  return (
    <span
      ref={wrapperRef}
      className={`info-tooltip-wrapper is-align-${align} is-placement-${placement} ${isActive ? 'is-active' : ''}`}
      onMouseEnter={() => setIsActive(true)}
      onMouseLeave={() => setIsActive(false)}
      onFocus={() => setIsActive(true)}
      onBlur={() => setIsActive(false)}
    >
      <span className="embed-icon is-tooltip">
        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="12" cy="12" r="10" />
          <path d="M12 16v-4" />
          <path d="M12 8h.01" />
        </svg>
      </span>
      <span ref={tooltipRef} className="info-tooltip">
        {text}
      </span>
    </span>
  );
}
